import type { FieldGroup } from './types.js';

export const employmentStatusOptions = [
  { value: 'employed', label: 'Employed' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'student', label: 'Student' },
  { value: 'homemaker', label: 'Homemaker' },
  { value: 'retired', label: 'Retired' },
  { value: 'daily_wage', label: 'Daily wage worker' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Occupation / Profession — exhaustive Indian profession list.
 * Mirrored from `dating-schema/src/profile-fields/03-basics.ts` (the
 * `profession` field, order 35) so cross-product profile data agrees on
 * the same canonical occupation values. Keep this list in sync with
 * dating-schema if either side adds a category.
 */
export const occupationOptions = [
  // Technology
  {
    value: 'it_software',
    label: 'IT & Software',
    description: 'Developer, data scientist, IT support, DevOps, etc.',
  },
  {
    value: 'engineering',
    label: 'Engineering',
    description: 'Civil, mechanical, electrical, chemical, etc.',
  },
  // Healthcare & Medicine
  {
    value: 'healthcare_doctor',
    label: 'Doctor / Physician',
    description: 'MBBS, MD, surgeon, dentist, etc.',
  },
  {
    value: 'healthcare_nursing',
    label: 'Nursing & Allied Health',
    description: 'Nurse, physiotherapist, lab tech, etc.',
  },
  {
    value: 'healthcare_pharma',
    label: 'Pharma & Biotech',
    description: 'Pharmaceutical, biotech, clinical research',
  },
  {
    value: 'psychology_therapy',
    label: 'Psychology & Therapy',
    description: 'Psychologist, counsellor, therapist',
  },
  // Education & Research
  {
    value: 'education_teaching',
    label: 'Teaching & Training',
    description: 'School teacher, professor, corporate trainer',
  },
  {
    value: 'science_research',
    label: 'Science & Research',
    description: 'Scientist, lab researcher, academic scholar',
  },
  // Finance & Business
  {
    value: 'finance_banking',
    label: 'Finance & Banking',
    description: 'Banking, investment, stock market, insurance',
  },
  {
    value: 'chartered_accountant',
    label: 'Chartered Accountant (CA)',
    description: 'CA, CS, CMA, auditor',
  },
  {
    value: 'business_management',
    label: 'Business & Management',
    description: 'Operations, consulting, HR, MBA roles',
  },
  // Creative & Media
  {
    value: 'arts_entertainment',
    label: 'Arts & Entertainment',
    description: 'Music, film, dance, fine arts, theatre',
  },
  {
    value: 'media_communication',
    label: 'Media & Communication',
    description: 'Journalism, PR, advertising, content',
  },
  {
    value: 'design_creative',
    label: 'Design & Creative',
    description: 'Graphic design, UI/UX, fashion design, architecture',
  },
  // Government & Public Service
  {
    value: 'civil_services',
    label: 'Civil Services (IAS/IPS/IFS)',
    description: 'Government officer cadre — IAS, IPS, IRS, IFS',
  },
  {
    value: 'government_public',
    label: 'Government / Public Sector',
    description: 'PSU, state govt, municipal, railway, postal',
  },
  {
    value: 'defence_military',
    label: 'Defence / Military',
    description: 'Army, Navy, Air Force, paramilitary, coast guard',
  },
  {
    value: 'police_law_enforcement',
    label: 'Police & Law Enforcement',
    description: 'Police, CBI, NIA, customs, excise',
  },
  // Legal
  {
    value: 'legal',
    label: 'Legal & Law',
    description: 'Lawyer, advocate, judge, legal advisor, notary',
  },
  // Trades & Services
  {
    value: 'skilled_trades',
    label: 'Skilled Trades',
    description: 'Electrician, plumber, carpenter, welder, mechanic',
  },
  {
    value: 'transport_logistics',
    label: 'Transport & Logistics',
    description: 'Driver, delivery, warehouse, supply chain',
  },
  {
    value: 'hospitality_travel',
    label: 'Hospitality & Tourism',
    description: 'Hotels, restaurants, airlines, travel agency',
  },
  {
    value: 'beauty_wellness',
    label: 'Beauty & Wellness',
    description: 'Salon, spa, fitness trainer, yoga instructor',
  },
  // Agriculture & Rural
  {
    value: 'agriculture_farming',
    label: 'Agriculture & Farming',
    description: 'Farming, agri-business, dairy, fisheries',
  },
  // Real Estate & Construction
  {
    value: 'real_estate',
    label: 'Real Estate & Construction',
    description: 'Property, builder, interior design, architecture',
  },
  // Retail & Commerce
  {
    value: 'retail_ecommerce',
    label: 'Retail & E-commerce',
    description: 'Shops, online stores, wholesale, distribution',
  },
  {
    value: 'manufacturing',
    label: 'Manufacturing & Production',
    description: 'Factory, production, quality control, industrial',
  },
  // Maritime
  {
    value: 'merchant_navy',
    label: 'Merchant Navy',
    description: 'Commercial shipping, maritime, offshore',
  },
  // Social & NGO
  {
    value: 'ngo_social_work',
    label: 'NGO & Social Work',
    description: 'Non-profit, social worker, community development',
  },
  {
    value: 'religious_spiritual',
    label: 'Religious / Spiritual Work',
    description: 'Priest, pujari, maulvi, pastor, monk, spiritual guide',
  },
  // Independent
  {
    value: 'freelancer_gig',
    label: 'Freelancer / Gig Worker',
    description: 'Independent contractor, gig economy, Uber/Ola driver',
  },
  {
    value: 'content_creator',
    label: 'Content Creator / Influencer',
    description: 'YouTuber, blogger, podcaster, Instagram creator',
  },
  {
    value: 'self_employed',
    label: 'Self-employed / Entrepreneur',
    description: 'Own business, startup founder, shop owner',
  },
  // Non-working
  { value: 'student', label: 'Student', description: 'Currently pursuing education' },
  { value: 'homemaker', label: 'Homemaker', description: 'Managing the household full-time' },
  { value: 'retired', label: 'Retired', description: 'No longer working professionally' },
  {
    value: 'not_working',
    label: 'Not Currently Working',
    description: 'Between jobs, career break, or job seeking',
  },
  {
    value: 'differently_abled_pension',
    label: 'On Disability / Pension',
    description: 'Receiving disability benefits or pension',
  },
  { value: 'other', label: 'Other', description: 'Something not listed above' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say', description: 'Keep this private' },
] as const;

export const shiftOptions = [
  { value: 'day_shift', label: 'Day shift' },
  { value: 'night_shift', label: 'Night shift' },
  { value: 'rotating', label: 'Rotating shifts' },
  { value: 'flexible', label: 'Flexible hours' },
  { value: 'work_from_home', label: 'Work from home' },
  { value: 'not_applicable', label: 'Not applicable' },
] as const;

export const incomeRangeOptions = [
  { value: 'below_5k', label: 'Below ₹5,000' },
  { value: '5k_10k', label: '₹5,000 – ₹10,000' },
  { value: '10k_25k', label: '₹10,000 – ₹25,000' },
  { value: '25k_50k', label: '₹25,000 – ₹50,000' },
  { value: '50k_1L', label: '₹50,000 – ₹1 Lakh' },
  { value: '1L_2_5L', label: '₹1 Lakh – ₹2.5 Lakh' },
  { value: '2_5L_5L', label: '₹2.5 Lakh – ₹5 Lakh' },
  { value: 'above_5L', label: 'Above ₹5 Lakh' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const comfortableWithShiftOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'somewhat', label: 'Somewhat' },
] as const;

export const employment: FieldGroup = {
  key: 'employment',
  label: 'Employment & Occupation',
  step: 7,
  description: 'Current job, shift, income, and dream occupation.',
  fields: [
    {
      order: 1,
      key: 'employmentStatus',
      label: 'Employment Status',
      type: 'single_select',
      step: 7,
      isOnboarding: true,
      optional: true,
      options: [...employmentStatusOptions],
    },
    {
      order: 2,
      key: 'occupation',
      label: 'Occupation',
      type: 'single_select',
      step: 7,
      isOnboarding: true,
      optional: true,
      options: [...occupationOptions],
    },
    {
      order: 3,
      key: 'workShift',
      label: 'Work Shift',
      type: 'single_select',
      step: 7,
      isOnboarding: true,
      optional: true,
      options: [...shiftOptions],
    },
    {
      order: 4,
      key: 'comfortableWithShift',
      label: 'Comfortable with Shift',
      type: 'single_select',
      step: 7,
      isOnboarding: true,
      optional: true,
      options: [...comfortableWithShiftOptions],
    },
    {
      order: 5,
      key: 'desiredShift',
      label: 'Desired Shift',
      type: 'single_select',
      step: 7,
      isOnboarding: true,
      optional: true,
      options: [...shiftOptions],
    },
    {
      order: 6,
      key: 'incomeRange',
      label: 'Income Range',
      type: 'single_select',
      step: 7,
      isOnboarding: true,
      optional: true,
      options: [...incomeRangeOptions],
      sensitive: true,
    },
    {
      order: 7,
      key: 'dreamOccupation',
      label: 'Dream Occupation',
      type: 'single_select',
      step: 7,
      isOnboarding: true,
      optional: true,
      // Same option set as `occupation` above — answers "If you could
      // start over, what would you do?" using the canonical profession
      // enum (mirrored from dating-schema). The two fields share both
      // values and translations via `optionNs: 'occupation'`.
      options: [...occupationOptions],
    },
  ],
};
